import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { syncWithFdsAdmin } from 'services/users'
import { useNotification } from 'components/Notification'
import { USERS_QUERY_KEY, USER_QUERY_KEY } from './useUsers'
import { CUSTOMER_AUTHORIZATION_QUERY_KEY } from 'pages/CustomerAccount/constants'

/**
 * Mutation hook for syncing a user with FDS Admin.
 * Tracks which row is currently syncing via syncingRowId.
 * @returns {object} - { syncUser, syncUserForRow, isSyncing, syncingRowId } where syncingRowId is the row ID currently being synced
 */
function useSyncUser() {
  const { createNotification } = useNotification()
  const queryClient = useQueryClient()
  const [syncingRowId, setSyncingRowId] = useState(null)

  const { mutate: syncUser, isPending: isSyncing } = useMutation({
    mutationFn: syncWithFdsAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_AUTHORIZATION_QUERY_KEY] })
      createNotification({
        message: 'Successfully synced user with FDS Admin',
        variant: 'success',
      })
    },
    onError: error => {
      const message =
        error?.response?.data?.errors?.[0] || error?.message || 'Failed to sync user with FDS Admin'
      createNotification({ message, variant: 'error' })
    },
    onSettled: () => setSyncingRowId(null),
  })

  const syncUserForRow = (rowId, serialNumbers) => {
    setSyncingRowId(rowId)
    syncUser(serialNumbers)
  }

  return { syncUser, syncUserForRow, isSyncing, syncingRowId }
}

export default useSyncUser
